// コネクタ（接続矢印）の幾何計算。
// アンカー / ルーティング / 保存 bbox の算出ロジックは web / api 双方で共用するため domain に移管した。
// ここでは domain のコアを再エクスポートしつつ、描画専用（SVG / Canvas）のヘルパーと、
// コネクタ作成 UI が共有する当たり判定の定数を定義する。

export {
  CONNECTOR_STUB_PX,
  CONNECTOR_BBOX_PAD_PX,
  sideAnchor,
  sideDirection,
  sideUnitVector,
  frameAABB,
  nearestSide,
  computeConnectorRoute,
  dedupePoints,
  routeBoundingBox,
  connectorStoredFrame,
} from "@refinement-canvas/domain";
export type { ConnectorFrame, ConnectorPoint } from "@refinement-canvas/domain";

import type { ConnectorPoint } from "@refinement-canvas/domain";

/**
 * 接続先の当たり判定を少しだけ広げる余白（ステージ座標 px）。
 * コネクタ作成 UI の吸着判定（findConnectTarget）と、コネクタからのオブジェクト自動作成の
 * 抑止判定が同じ値を使うため、双方が依存できる中立なこのモジュールが正本として持つ。
 */
export const CONNECTOR_TARGET_HIT_PAD_PX = 6;

// 角を丸める半径の最小判定に使う許容誤差。
const EPSILON = 0.01;

/**
 * 直交折れ線の各角を半径 radius で丸めた SVG パス（d 属性文字列）を返す。
 * 始点から終点まで、内側の各頂点を二次ベジェで丸める。
 */
export function buildRoundedPath(points: readonly ConnectorPoint[], radius: number): string {
  if (points.length === 0) return "";
  if (points.length === 1) {
    const p = points[0]!;
    return `M ${p.x} ${p.y}`;
  }
  const first = points[0]!;
  let d = `M ${first.x} ${first.y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = points[i - 1]!;
    const curr = points[i]!;
    const next = points[i + 1]!;
    // curr の手前 / 先で、それぞれ隣接辺の長さの半分を上限に radius だけ手前で曲げる。
    const inLen = Math.hypot(curr.x - prev.x, curr.y - prev.y);
    const outLen = Math.hypot(next.x - curr.x, next.y - curr.y);
    const r = Math.min(radius, inLen / 2, outLen / 2);
    if (r < EPSILON) {
      d += ` L ${curr.x} ${curr.y}`;
      continue;
    }
    const inUnit = { x: (curr.x - prev.x) / inLen, y: (curr.y - prev.y) / inLen };
    const outUnit = { x: (next.x - curr.x) / outLen, y: (next.y - curr.y) / outLen };
    const start = { x: curr.x - inUnit.x * r, y: curr.y - inUnit.y * r };
    const end = { x: curr.x + outUnit.x * r, y: curr.y + outUnit.y * r };
    d += ` L ${start.x} ${start.y} Q ${curr.x} ${curr.y} ${end.x} ${end.y}`;
  }
  const last = points[points.length - 1]!;
  d += ` L ${last.x} ${last.y}`;
  return d;
}

/**
 * 矢じり（先端 tip と、その直前の点 from から向きを決める）の 2 本のヒゲ端点を返す。
 * tip に向かって size の長さで ±angle 開いた点を返す。描画側で tip と結んで矢じりにする。
 */
export function arrowHeadPoints(
  tip: ConnectorPoint,
  from: ConnectorPoint,
  size: number,
  angleDeg = 26
): { readonly left: ConnectorPoint; readonly right: ConnectorPoint } | null {
  const dx = tip.x - from.x;
  const dy = tip.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < EPSILON) return null;
  const ux = dx / len;
  const uy = dy / len;
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // tip から逆向き（-u）へ size 進め、±angle 回転した 2 点。
  const bx = -ux;
  const by = -uy;
  const left = {
    x: tip.x + (bx * cos - by * sin) * size,
    y: tip.y + (bx * sin + by * cos) * size,
  };
  const right = {
    x: tip.x + (bx * cos + by * sin) * size,
    y: tip.y + (-bx * sin + by * cos) * size,
  };
  return { left, right };
}
